'use client';

import { createClient } from '@/lib/supabase/client';

interface Service {
  id: string;
  title: string;
  slug: string;
  description: string;
  features: string[];
  pricing?: string;
  benefits: string[];
  callToAction: {
    title: string;
    description: string;
    primaryButton: { text: string; link: string };
    secondaryButton: { text: string; link: string };
  };
  featuredImageUrl?: string;
  metaTitle?: string;
  metaDescription?: string;
  status: 'active' | 'inactive';
  displayOrder: number;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '08') return true;
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

export const servicesService = {
  async getAll(): Promise<Service[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (data || []).map((row) => ({
        id: row.id,
        title: row.title,
        slug: row.slug,
        description: row.description,
        features: row.features || [],
        pricing: row.pricing,
        benefits: row.benefits || [],
        callToAction: row.call_to_action || {},
        featuredImageUrl: row.featured_image_url,
        metaTitle: row.meta_title,
        metaDescription: row.meta_description,
        status: row.status,
        displayOrder: row.display_order,
        createdBy: row.created_by,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      }));
    } catch (error: any) {
      console.error('Get services error:', error.message);
      throw error;
    }
  },

  async getAllPublic(): Promise<Service[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('status', 'active')
        .order('display_order', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (data || []).map((row) => ({
        id: row.id,
        title: row.title,
        slug: row.slug,
        description: row.description,
        features: row.features || [],
        pricing: row.pricing,
        benefits: row.benefits || [],
        callToAction: row.call_to_action || {},
        featuredImageUrl: row.featured_image_url,
        metaTitle: row.meta_title,
        metaDescription: row.meta_description,
        status: row.status,
        displayOrder: row.display_order,
        createdBy: row.created_by,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      }));
    } catch (error: any) {
      console.error('Get public services error:', error.message);
      throw error;
    }
  },

  async getBySlug(slug: string): Promise<Service | null> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'active')
        .maybeSingle();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return null;
        }
      }

      if (!data) return null;

      return {
        id: data.id,
        title: data.title,
        slug: data.slug,
        description: data.description,
        features: data.features || [],
        pricing: data.pricing,
        benefits: data.benefits || [],
        callToAction: data.call_to_action || {},
        featuredImageUrl: data.featured_image_url,
        metaTitle: data.meta_title,
        metaDescription: data.meta_description,
        status: data.status,
        displayOrder: data.display_order,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      console.error('Get service by slug error:', error.message);
      throw error;
    }
  },

  async create(service: Omit<Service, 'id' | 'createdAt' | 'updatedAt'>): Promise<Service | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data, error } = await supabase
        .from('services')
        .insert({
          title: service.title,
          slug: service.slug,
          description: service.description,
          features: service.features,
          pricing: service.pricing,
          benefits: service.benefits,
          call_to_action: service.callToAction,
          featured_image_url: service.featuredImageUrl,
          meta_title: service.metaTitle,
          meta_description: service.metaDescription,
          status: service.status,
          display_order: service.displayOrder,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;

      return data ? {
        id: data.id,
        title: data.title,
        slug: data.slug,
        description: data.description,
        features: data.features || [],
        pricing: data.pricing,
        benefits: data.benefits || [],
        callToAction: data.call_to_action || {},
        featuredImageUrl: data.featured_image_url,
        metaTitle: data.meta_title,
        metaDescription: data.meta_description,
        status: data.status,
        displayOrder: data.display_order,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      } : null;
    } catch (error: any) {
      console.error('Create service error:', error.message);
      throw error;
    }
  },

  async update(id: string, updates: Partial<Omit<Service, 'id' | 'createdAt' | 'updatedAt'>>): Promise<Service | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const updateData: any = {};
      if (updates.title !== undefined) updateData.title = updates.title;
      if (updates.slug !== undefined) updateData.slug = updates.slug;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.features !== undefined) updateData.features = updates.features;
      if (updates.pricing !== undefined) updateData.pricing = updates.pricing;
      if (updates.benefits !== undefined) updateData.benefits = updates.benefits;
      if (updates.callToAction !== undefined) updateData.call_to_action = updates.callToAction;
      if (updates.featuredImageUrl !== undefined) updateData.featured_image_url = updates.featuredImageUrl;
      if (updates.metaTitle !== undefined) updateData.meta_title = updates.metaTitle;
      if (updates.metaDescription !== undefined) updateData.meta_description = updates.metaDescription;
      if (updates.status !== undefined) updateData.status = updates.status;
      if (updates.displayOrder !== undefined) updateData.display_order = updates.displayOrder;

      const { data, error } = await supabase
        .from('services')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return data ? {
        id: data.id,
        title: data.title,
        slug: data.slug,
        description: data.description,
        features: data.features || [],
        pricing: data.pricing,
        benefits: data.benefits || [],
        callToAction: data.call_to_action || {},
        featuredImageUrl: data.featured_image_url,
        metaTitle: data.meta_title,
        metaDescription: data.meta_description,
        status: data.status,
        displayOrder: data.display_order,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      } : null;
    } catch (error: any) {
      console.error('Update service error:', error.message);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { error } = await supabase
        .from('services')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error: any) {
      console.error('Delete service error:', error.message);
      throw error;
    }
  },
};